import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, imgSrc, fmtMoney, fmtDate, usageLabel, channelLabel } from "@/lib/api";
import { FileKey, Brush, Lock, Flag, Loader2, BadgeCheck, Wand2, Megaphone, ArrowUpRight } from "lucide-react";

const TIERS = [
  { value: "social_ads", label: "Social Media Ads", pct: 40, desc: "Paid social campaigns (Meta, TikTok, Pinterest ads)" },
  { value: "print_editorial", label: "Print & Editorial", pct: 60, desc: "Magazines, catalogues, lookbooks, press" },
  { value: "billboard_ooh", label: "Billboard / Out-of-home", pct: 100, desc: "Billboards, transit, storefronts, large-format OOH" },
];

const TIER_LABELS = Object.fromEntries(TIERS.map((t) => [t.value, t.label]));

export const LicenseCard = ({ license, mySide }) => {
  const navigate = useNavigate();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reported, setReported] = useState(false);
  const [upOpen, setUpOpen] = useState(false);
  const [upTier, setUpTier] = useState("social_ads");
  const [upBusy, setUpBusy] = useState(false);

  const requestUpcharge = async () => {
    setUpBusy(true);
    try {
      const { data } = await api.post("/upcharge", { license_id: license.id, tier: upTier });
      setUpOpen(false);
      toast.success(`Upgrade requested — ${fmtMoney(data.amount)} escrowed. ${license.model_name} gets an instant notification to approve or decline.`);
    } catch (e) {
      toast.error(errMsg(e, "Could not request the upgrade"));
    } finally {
      setUpBusy(false);
    }
  };

  const fileReport = async () => {
    if (reportText.trim().length < 10) {
      toast.error("Describe the breach in at least 10 characters");
      return;
    }
    setReportBusy(true);
    try {
      await api.post("/reports", { license_id: license.id, description: reportText });
      setReported(true);
      setReportOpen(false);
      toast.success("Breach report filed - our team will review it. Upheld breaches trigger penalties and licence revocation.");
    } catch (e) {
      toast.error(errMsg(e, "Could not file report"));
    } finally {
      setReportBusy(false);
    }
  };

  const now = Date.now();
  const start = new Date(license.start_at).getTime();
  const end = new Date(license.end_at).getTime();
  const total = Math.max(end - start, 1);
  const elapsed = Math.min(Math.max(now - start, 0), total);
  const pct = Math.round((elapsed / total) * 100);
  const daysLeft = Math.max(Math.ceil((end - now) / 86400000), 0);
  const img = imgSrc(license.model_image);

  return (
    <Card data-testid="license-card" className="overflow-hidden">
      <CardContent className="flex gap-4 p-4">
        <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg bg-muted">
          {img && <img src={img} alt={license.model_name} className="h-full w-full object-cover" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-display text-lg leading-tight">{license.model_name}</h3>
              <p className="text-xs text-muted-foreground">
                {mySide === "model" ? `Licensed to ${license.client_company_name}` : license.terms?.job_title}
              </p>
            </div>
            <StatusBadge status={license.status} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="rounded-full text-[10px]">{usageLabel(license.terms?.usage_type)}</Badge>
            <Badge variant="secondary" className="rounded-full gap-1 text-[10px]">
              <Brush className="h-3 w-3" /> retouching {license.terms?.retouching_allowed ? "consented" : "not allowed"}
            </Badge>
            {license.terms?.exclusivity && (
              <Badge variant="secondary" className="rounded-full gap-1 text-[10px]"><Lock className="h-3 w-3" /> exclusive</Badge>
            )}
            {(license.terms?.upcharge_tiers || []).map((t) => (
              <Badge key={t} data-testid="license-tier-badge" className="rounded-full gap-1 bg-[hsl(174,38%,34%)] text-[10px] hover:bg-[hsl(174,38%,34%)]">
                <ArrowUpRight className="h-3 w-3" /> {TIER_LABELS[t] || t}
              </Badge>
            ))}
          </div>
          {(license.terms?.media_channels || []).length > 0 && (
            <div data-testid="license-media-channels" className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Licensed media:</span>
              {license.terms.media_channels.map((c) => (
                <Badge key={c} variant="outline" className="rounded-full border-[hsl(174,38%,60%)] text-[10px]">
                  {channelLabel(c)}
                </Badge>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-mono-plex text-muted-foreground">
                {fmtDate(license.start_at)} → {fmtDate(license.end_at)}
              </span>
              <span data-testid="license-countdown" className="font-mono-plex font-medium">
                {license.status === "active" ? `${daysLeft}d remaining` : "ended"}
              </span>
            </div>
            <Progress value={license.status === "active" ? pct : 100} className="h-1.5" />
          </div>
          <div className="flex items-center justify-between">
            <Link
              to={`/verify?id=${license.id}`}
              data-testid="license-registry-link"
              title="Public Ethical AI Registry - anyone can verify this licence"
              className="flex items-center gap-1 font-mono-plex text-[10px] text-[hsl(174,38%,30%)] underline-offset-2 hover:underline"
            >
              <BadgeCheck className="h-3 w-3" /> Registry {license.id.slice(0, 8).toUpperCase()}
            </Link>
            <span className="text-sm font-semibold">{fmtMoney(license.amount_paid)} paid upfront</span>
          </div>
          <div className="flex items-center justify-between gap-2 border-t pt-2">
            <span className="flex items-center gap-1 font-mono-plex text-[10px] text-muted-foreground">
              <FileKey className="h-3 w-3" /> Bound by Brand Licensing Agreement
            </span>
            <div className="flex items-center gap-1.5">
              {mySide === "client" && license.status === "active" && (
                <>
                  <Button data-testid="license-open-studio-button" size="sm"
                    onClick={() => navigate(`/studio?license=${license.id}`)}
                    className="h-7 rounded-lg text-xs">
                    <Wand2 className="mr-1 h-3 w-3" /> Open in Studio
                  </Button>
                  <Dialog open={upOpen} onOpenChange={setUpOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="license-upcharge-button" size="sm" variant="outline"
                        className="h-7 rounded-lg border bg-card text-xs hover:bg-muted">
                        <Megaphone className="mr-1 h-3 w-3" /> Tier upgrade
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-display text-xl">Request a higher usage tier</DialogTitle>
                        <DialogDescription>
                          This licence covers web e-commerce by default. Upgrades are priced as a percentage of the
                          original fee ({fmtMoney(license.amount_paid)}), escrowed from your wallet immediately, and
                          {" "}{license.model_name} gets an instant notification to approve or decline.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2">
                        {TIERS.map((t) => {
                          const covered = (license.terms?.upcharge_tiers || []).includes(t.value);
                          return (
                            <button key={t.value} type="button" disabled={covered}
                              data-testid={`license-upcharge-tier-${t.value}`}
                              onClick={() => setUpTier(t.value)}
                              className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left text-sm transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                covered ? "opacity-50" : upTier === t.value ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]" : "bg-card hover:bg-muted"
                              }`}>
                              <span>
                                <span className="block font-semibold">{t.label} {covered && "(already covered)"}</span>
                                <span className="block text-xs text-muted-foreground">{t.desc}</span>
                              </span>
                              <span className="shrink-0 font-mono-plex text-xs font-semibold">
                                +{t.pct}% = {fmtMoney(license.amount_paid * t.pct / 100)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <DialogFooter>
                        <Button data-testid="license-upcharge-submit-button" onClick={requestUpcharge} disabled={upBusy} className="rounded-xl">
                          {upBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Escrow {fmtMoney(license.amount_paid * (TIERS.find((t) => t.value === upTier)?.pct || 0) / 100)} & request
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              <Dialog open={reportOpen} onOpenChange={setReportOpen}>
              <DialogTrigger asChild>
                <Button data-testid="license-report-breach-button" size="sm" variant="ghost" disabled={reported}
                  className="h-7 rounded-lg text-xs text-destructive hover:bg-[hsl(6,62%,96%)]">
                  <Flag className="mr-1 h-3 w-3" /> {reported ? "Report filed" : "Report a breach"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">Report a breach of agreement</DialogTitle>
                  <DialogDescription>
                    Describe how the {mySide === "model" ? "client" : "other party"} has breached the licence terms
                    (e.g. usage outside declared media, banned category, alterations, use after expiry). Upheld reports
                    trigger automated penalties, possible charges, forfeiture of refunds, and licence revocation.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  data-testid="license-report-description-input"
                  rows={4}
                  placeholder="What happened, where did you see it, and when…"
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  className="rounded-xl bg-card"
                />
                <DialogFooter>
                  <Button data-testid="license-report-submit-button" onClick={fileReport} disabled={reportBusy} className="rounded-xl">
                    {reportBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} File breach report
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
